/**
 * The listener for `columns/cell-action.tsx` — parity card **P-128**.
 *
 * Three tier-2 types have an affordance in the cell that monday's API refuses as a value write
 * `[09§A.7]`: the `button` column's button, `time_tracking`'s Start/Stop and its manual **Log
 * time**, and `file`'s upload. Each dispatches a `CustomEvent` rather than importing the store
 * (CLAUDE.md rule 6) — and for three rounds **nothing received it**, so all three did nothing
 * at all. This is the other half of that seam: one subscription, owned by the board screen
 * where the store and the source live, mapping each kind onto the procedure that exists:
 *
 * | kind | verb |
 * |---|---|
 * | `button-click` | `cells.clickButton` (writes monday's ordinary `update_column_value`) |
 * | `timer-start` / `timer-stop` | `cells.timer.start` / `.stop` |
 * | `timer-log` | `cells.timer.addSession` |
 * | `file-upload` | the upload route (`add_file_to_column`) |
 *
 * Each one re-reads the board afterwards, the way every other mutation in the grid does: the
 * new value (a click count, a duration, a file list) is the server's to compute.
 */
import { useEffect } from 'react';
import type { ColumnId, ItemId } from '@elizmonday/shared';
import { onCellActionRequested, type CellActionRequest } from '../columns/cell-action';
import { useTable, useTableActions, useTableContext } from './context';
import type { BoardDataSource } from '../data/types';

/** What each kind could not do, said the way the grid says every other refused write. */
const FAILURE: Record<CellActionRequest['kind'], string> = {
  'button-click': 'That button could not be pressed',
  'timer-start': 'The timer could not be started',
  'timer-stop': 'The timer could not be stopped',
  'timer-log': 'That session could not be logged',
  'file-upload': 'That file could not be uploaded',
};

export async function runCellAction(
  source: BoardDataSource,
  boardId: string,
  request: CellActionRequest,
): Promise<void> {
  const ref = {
    boardId: boardId as unknown as Parameters<NonNullable<BoardDataSource['clickButton']>>[0]['boardId'],
    itemId: request.itemId as ItemId,
    columnId: request.columnId as ColumnId,
  };
  switch (request.kind) {
    case 'button-click':
      await source.clickButton?.(ref);
      return;
    case 'timer-start':
      await source.setTimerRunning?.({ ...ref, running: true });
      return;
    case 'timer-stop':
      await source.setTimerRunning?.({ ...ref, running: false });
      return;
    case 'timer-log': {
      const session = request.session;
      if (!session) return;
      await source.addTimerSession?.({ ...ref, ...session });
      return;
    }
    case 'file-upload': {
      const file = request.file;
      if (!file) return;
      await source.uploadCellFile?.({ ...ref, file });
      return;
    }
  }
}

/** Subscribes once, for as long as the table view is mounted. */
export function useCellActions(): void {
  const { source } = useTableContext();
  const actions = useTableActions();
  const boardId = useTable((state) => state.payload?.board.id ?? null);

  useEffect(() => {
    if (boardId === null) return;
    return onCellActionRequested((request) => {
      void (async () => {
        try {
          await runCellAction(source, String(boardId), request);
          // The affordance changed a cell the server owns; re-read it like every other write.
          await actions.load();
        } catch (error) {
          actions.showToast({
            message: error instanceof Error ? error.message : FAILURE[request.kind],
            kind: 'error',
          });
        }
      })();
    });
  }, [source, actions, boardId]);
}
